import { ArrowUpRight, Check } from "lucide-react";
import { realWorldPage } from "../mock";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Reveal from "./Reveal";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import SectionOrnament from "./SectionOrnament";

export default function RealWorldPage() {
  const { hero, intro, pillars, skills, outcomes } = realWorldPage;

  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero eyebrow={hero.eyebrow} title={hero.title} italic={hero.italic} image={hero.image} blurb={hero.blurb} />

          {/* Intro */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
              <Reveal>
                <p className="text-[16px] md:text-[18px] leading-[1.85] text-[var(--ink)]/80 serif">
                  {intro}
                </p>
              </Reveal>
              <SectionOrnament />
            </div>
          </section>

          {/* Pillars */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-12">
                  <div className="eyebrow mb-4">Three Pillars</div>
                  <h2 className="h-section text-[var(--ink)]">
                    What makes the
                    <span className="block serif-italic gold-text">curriculum signature.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {pillars.map((p, i) => (
                  <Reveal key={p.title} variant="zoom" delay={i * 120}>
                    <article className="light-card overflow-hidden h-full flex flex-col">
                      <div className="aspect-[4/3] overflow-hidden bg-[var(--navy)]">
                        <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover img-lift" />
                      </div>
                      <div className="p-6 md:p-7 flex-1">
                        <div className="eyebrow mb-2">0{i + 1}</div>
                        <h3 className="serif text-[22px] md:text-[26px] text-[var(--ink)] leading-tight">
                          {p.title}
                          <span className="block serif-italic gold-text text-[16px] md:text-[18px] mt-0.5">designed to last.</span>
                        </h3>
                        <span className="gold-divider-thin block my-4" />
                        <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75">{p.body}</p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <PullQuote
            text="Education is not the filling of a pail, but the lighting of a fire."
            attribution="W. B. Yeats"
          />

          {/* 21st Century Skills */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">21st-Century Skills</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Skills that
                    <span className="serif-italic gold-text"> transcend the classroom.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {skills.map((s, i) => (
                  <Reveal key={s.title} delay={(i % 3) * 100}>
                    <div className="h-full p-6 md:p-7 border border-[rgba(184,148,86,0.25)] light-card">
                      <div className="eyebrow mb-3">0{i + 1}</div>
                      <h3 className="serif text-[22px] md:text-[26px] text-[var(--ink)] leading-tight">
                        {s.title.split(" ")[0]}
                        <span className="block serif-italic gold-text text-[18px] md:text-[20px]">
                          {s.title.split(" ").slice(1).join(" ")}
                        </span>
                      </h3>
                      <span className="gold-divider-thin block my-4" />
                      <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75">{s.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Learning Outcomes */}
          <section className="relative py-14 md:py-20 overflow-hidden" style={{ background: "var(--navy-deep)" }}>
            <div className="mandala-bg" style={{ opacity: 0.35 }} />
            <div className="relative max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Learning Outcomes</div>
                  <h2 className="h-section text-white">
                    A child ready for
                    <span className="block serif-italic gold-text">the real world.</span>
                  </h2>
                  <span className="gold-divider mx-auto" />
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {outcomes.map((o, i) => (
                  <Reveal key={o.title} delay={(i % 3) * 100}>
                    <div className="dark-card p-6 md:p-7 h-full">
                      <Check size={20} className="text-[var(--gold)]" strokeWidth={1.4} />
                      <h3 className="serif text-white text-[22px] md:text-[26px] leading-tight mt-3">
                        {o.title}
                      </h3>
                      <span className="gold-divider-thin block my-4" />
                      <p className="text-[14.5px] leading-[1.7] text-white/75">{o.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal>
                <div className="mt-12 md:mt-14 text-center">
                  <button onClick={openEnquire} className="btn-gold inline-flex items-center gap-2" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Apply Now <ArrowUpRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          <RelatedPages current="/real-world.html" />
        </>
      )}
    </PageShell>
  );
}
